import { put, call, takeLatest } from "redux-saga/effects";
import * as apis from "../../network/apis/categories";
import * as types from "./types";
import * as actions from "./actions";
import { showErrorMsg, showSuccessMsg } from "../../utils/Helpers";
import { ROUTE_PATHS } from "../../utils/RoutesPaths";
import { deleteBlob } from "../../utils/blobStorageServices";

function* approvedCategoriesListSaga({ payload }) {
  try {
    const response = yield call(apis.approvedCategoriesList, payload);
    const { Items, CurrentPage, ItemCount, TotalPages } =
      response.data.Data;
    yield put(
      actions.approvedCategoriesListResponse({
        list: Items,
        meta: {
          currentPage: +CurrentPage,
          count: +ItemCount,
          pages: +TotalPages
        }
      })
    );
    if (CurrentPage && TotalPages && +CurrentPage > +TotalPages) {
      yield put(
        actions.approvedCategoriesListRequest({
          ...payload,
          page: +TotalPages
        })
      );
    }
  } catch (error) {
    yield error.response?.data?.Message &&
      showErrorMsg(error.response?.data?.Message);
  }
}

function* addCategorySaga({ payload }) {
  const { data, navigate } = payload;
  try {
    yield call(apis.addCategory, data);
    yield showSuccessMsg("addCategorySuccess");
    yield navigate(ROUTE_PATHS.approvedCategories);
  } catch (error) {
    yield error?.response?.data?.Message &&
      showErrorMsg(error.response.data?.Message);
    yield deleteBlob(data?.AdminCoverImage, "images");
    yield deleteBlob(data?.WebsiteCoverImage, "images");
  }
}

function* editCategorySaga({ payload }) {
  const {
    data,
    dbData: {
      adminCoverImageIsNew,
      dbAdminListCoverImage,
      categoryListCoverImageIsNew,
      dbCategoryListCoverImage
    },
    navigate,
    id,
    isRequest
  } = payload;
  try {
    yield call(apis.editCategory, data);
    yield showSuccessMsg("editCategorySuccess");
    if (adminCoverImageIsNew && !!dbAdminListCoverImage) {
      yield deleteBlob(
        dbAdminListCoverImage?.split("/images/")[1],
        "images"
      );
    }
    if (categoryListCoverImageIsNew && !!dbCategoryListCoverImage) {
      yield deleteBlob(
        dbCategoryListCoverImage?.split("/images/")[1],
        "images"
      );
    }
    if (isRequest) {
      yield put(
        actions.acceptCategoryRequest({ data: { id }, navigate })
      );
    } else {
      yield navigate(
        ROUTE_PATHS.categoryDetailsApproved.replace(":id", id)
      );
    }
  } catch (error) {
    yield error?.response?.data?.Message &&
      showErrorMsg(error.response.data?.Message);
    const { data, dbData } = payload;
    if (dbData.adminCoverImageIsNew && !!data?.AdminCoverImage)
      yield deleteBlob(data?.AdminCoverImage, "images");
    if (
      dbData.categoryListCoverImageIsNew &&
      !!data?.WebsiteCoverImage
    )
      yield deleteBlob(data?.WebsiteCoverImage, "images");
  }
}

function* getCategoryDetailsSaga({ payload }) {
  try {
    const response = yield call(apis.getCategoryDetails, payload);
    yield put(actions.getCategoryDetailsResponse(response.data.Data));
  } catch (error) {
    yield error?.response?.data?.Message &&
      showErrorMsg(error.response.data?.Message);
  }
}

function* deleteCategorySaga({ payload }) {
  try {
    yield call(apis.deleteCategory, payload);
    yield showSuccessMsg("deleteCategorySuccess");
    payload.src === ROUTE_PATHS.categoryDetailsApproved
      ? yield payload.navigate(ROUTE_PATHS.approvedCategories)
      : yield put(
          actions.approvedCategoriesListRequest({
            params: payload.params
          })
        );
  } catch (error) {
    yield error?.response?.data?.Message &&
      showErrorMsg(error.response.data?.Message);
  }
}

function* categorySubCategoriesListSaga({ payload }) {
  try {
    const response = yield call(
      apis.categorySubCategoriesList,
      payload
    );
    const { Items, CurrentPage, ItemCount, TotalPages } =
      response.data.Data;
    yield put(
      actions.categorySubCategoriesListResponse({
        list: Items,
        meta: {
          currentPage: +CurrentPage,
          count: +ItemCount,
          pages: +TotalPages
        }
      })
    );
    if (CurrentPage && TotalPages && +CurrentPage > +TotalPages) {
      yield put(
        actions.categorySubCategoriesListRequest({
          ...payload,
          page: +TotalPages
        })
      );
    }
  } catch (error) {
    yield error.response?.data?.Message &&
      showErrorMsg(error.response?.data?.Message);
  }
}

function* requestsCategoriesListSaga({ payload }) {
  try {
    const response = yield call(apis.requestsCategoriesList, payload);
    const { Items, CurrentPage, ItemCount, TotalPages } =
      response.data.Data;
    yield put(
      actions.requestsCategoriesListResponse({
        list: Items,
        meta: {
          currentPage: +CurrentPage,
          count: +ItemCount,
          pages: +TotalPages
        }
      })
    );
    if (CurrentPage && TotalPages && +CurrentPage > +TotalPages) {
      yield put(
        actions.requestsCategoriesListRequest({
          ...payload,
          page: +TotalPages
        })
      );
    }
  } catch (error) {
    yield error.response?.data?.Message &&
      showErrorMsg(error.response?.data?.Message);
  }
}

function* getCategoryRequestDetailsSaga({ payload }) {
  try {
    const response = yield call(
      apis.requestsCategoriesDetails,
      payload
    );
    yield put(
      actions.getCategoryRequestDetailsResponse(response.data.Data)
    );
  } catch (error) {
    yield error?.response?.data?.Message &&
      showErrorMsg(error.response.data?.Message);
  }
}

function* rejectCategoryRequestSaga({ payload }) {
  try {
    yield call(apis.rejectCategoryRequest, payload);
    yield showSuccessMsg("rejectCategoryRequestSuccessfully");
    yield payload.navigate(ROUTE_PATHS.categoriesRequests);
  } catch (error) {
    yield error?.response?.data?.Message &&
      showErrorMsg(error.response.data?.Message);
  }
}

function* acceptCategoryRequestSaga({ payload }) {
  try {
    yield call(apis.acceptCategoryRequest, payload.data);
    yield showSuccessMsg("acceptCategoryRequestSuccessfully");
    yield payload.navigate(ROUTE_PATHS.categoriesRequests);
  } catch (error) {
    yield error?.response?.data?.Message &&
      showErrorMsg(error.response.data?.Message);
  }
}

export default function* CategoriesSagas() {
  yield takeLatest(
    types.APPROVED_CATEGORIES_LIST_REQUEST,
    approvedCategoriesListSaga
  );
  yield takeLatest(types.ADD_CATEGORY_REQUEST, addCategorySaga);
  yield takeLatest(types.EDIT_CATEGORY_REQUEST, editCategorySaga);
  yield takeLatest(
    types.GET_CATEGORY_DETAILS_REQUEST,
    getCategoryDetailsSaga
  );
  yield takeLatest(types.DELETE_CATEGORY_REQUEST, deleteCategorySaga);
  yield takeLatest(
    types.CATEGORY_SUB_CATEGORIES_LIST_REQUEST,
    categorySubCategoriesListSaga
  );
  yield takeLatest(
    types.REQUESTS_CATEGORIES_LIST_REQUEST,
    requestsCategoriesListSaga
  );
  yield takeLatest(
    types.GET_CATEGORY_REQUEST_DETAILS_REQUEST,
    getCategoryRequestDetailsSaga
  );
  yield takeLatest(
    types.REJECT_CATEGORY_REQUEST,
    rejectCategoryRequestSaga
  );
  yield takeLatest(
    types.ACCEPT_CATEGORY_REQUEST,
    acceptCategoryRequestSaga
  );
}
